<template>
    <ul class="sportsSwitch-sportsList">
        <!-- To add a new zone entry: do not add new elements in template; add to zoneEntries instead -->
        <!-- Ticket link: https://ironman.atlassian.net/browse/SD-236322 -->
        <component
            v-bind:is="entry.component"
            v-bind="entry.props"
            v-for="entry in visibleZoneEntries"
            v-bind:key="entry.id"
            v-on:click="handleZoneEntryClick(entry)"
        />

        <Spinner v-if="loading" />

        <li
            v-for="info in sportsInfo"
            v-bind:key="info.sportType"
            class="sportsSwitch-sportItem"
            v-bind:class="[
                { _disabled: !(isVirtualSport(info.sportType) || isESports(info.sportType) || isMuayThaiForDisplay(info.sportType)) && !hasEvents(info) },
                { _eSports: isESports(info.sportType) },
                { '_has-sub-item': !!(info.infos && info.infos.length) },
                `${getGaTag(info)}`,
            ]"
        >
            <div v-if="isVirtualSport(info.sportType)" v-on:click="selectSport(info)">
                <SvgIcon v-bind:iconName="`sportIcon_${invertCapitalize(getSportTypeKey(info.sportType))}`" />
                <span>{{ i18n.t(`sportType_${getSportTypeKey(info.sportType)}`) }}</span>
                <SportListItemNewTag v-if="info.isNew" />
            </div>
            <div v-if="isESports(info.sportType)">
                <SvgIcon iconName="sportIcon_eSports" />
                <span>{{ i18n.t(`eSportType_${getSportTypeKey(info.sportType)}`) }} </span>
            </div>
            <ul
                v-if="isESports(info.sportType)"
                v-bind:class="{ '_single-sub-item': (info.infos?.length ?? 0) === 1 }"
            >
                <li
                    v-for="subInfo in info.infos"
                    v-bind:key="subInfo.sportType"
                    v-bind:class="[
                        { _disabled: isImEsports(subInfo.sportType) ? false : !subInfo.hasEvents },
                        { hideEsportsIM: isIomB2c },
                        `${getGaTag(subInfo)}`,
                    ]"
                >
                    <div v-on:click="selectSport(subInfo)">
                        <SvgIcon iconName="systemIcon_arrowRight" style="width: 1rem;" />
                        <span>{{ i18n.t(`eSportType_${getSportTypeKey(subInfo.sportType)}`) }}</span>
                        <SportListItemNewTag v-if="subInfo.isNew" />
                    </div>
                </li>
            </ul>
            <div v-if="isExchange(info.sportType)" v-on:click="selectSport(info)">
                <SvgIcon iconName="sportIcon_exchange" />
                <span>{{ i18n.t('exchange') }}</span>
                <SportListItemNewTag v-if="info.isNew" />
            </div>
            <div v-if="isMuayThaiForDisplay(info.sportType)">
                <SvgIcon iconName="sportIcon_muayThai" />
                <span>{{ i18n.t('sportType_MuayThai') }}</span>
            </div>
            <ul
                v-if="isMuayThaiForDisplay(info.sportType)"
                v-bind:class="{ '_single-sub-item': (info.infos?.length ?? 0) === 1 }"
            >
                <li
                    v-for="subInfo in info.infos"
                    v-bind:key="subInfo.sportType"
                    v-bind:class="[
                        { _disabled: isMuayThaiOneBx(subInfo.sportType) ? false : !subInfo.hasEvents },
                        `${getGaTag(subInfo)}`,
                    ]"
                >
                    <div v-on:click="selectSport(subInfo)">
                        <SvgIcon iconName="systemIcon_arrowRight" style="width: 1rem;" />
                        <span>{{ i18n.t(getMuayThaiSubLabelKey(subInfo.sportType)) }}</span>
                        <SportListItemNewTag v-if="subInfo.isNew" />
                    </div>
                </li>
            </ul>
            <div v-if="!(isVirtualSport(info.sportType) || isESports(info.sportType) || isExchange(info.sportType) || isMuayThaiForDisplay(info.sportType))" v-on:click="selectSport(info)">
                <SvgIcon v-bind:iconName="`sportIcon_${invertCapitalize(getSportTypeKey(info.sportType))}`" />
                <span>{{ i18n.t(`sportType_${getSportTypeKey(info.sportType)}`) }}</span>
                <SportListItemLiveTag v-if="hasLive(info)" />
                <SportListItemNewTag v-if="info.isNew" />
            </div>
        </li>
    </ul>
</template>

<script lang="ts" setup>
    import Cookies from 'js-cookie';
    import { storeToRefs } from 'pinia';
    import type { Component } from 'vue';
    import { computed, onMounted, onUnmounted, watch } from 'vue';
    import { useRoute, useRouter } from 'vue-router';
    import SportListItemLiveTag from '@/components/SportListItemLiveTag.vue';
    import SportListItemNewTag from '@/components/SportListItemNewTag.vue';
    import Spinner from '@/components/common/Spinner.vue';
    import MultipleSportsSportListEntrance from '@/components/sportsSwitch/MultipleSportsSportListEntrance.vue';
    import SportsCupSportListEntrance from '@/components/sportsSwitch/SportsCupSportListEntrance.vue';
    import TournamentCenterSportListEntrance from '@/components/tournamentCenter/entrance/TournamentCenterSportListEntrance.vue';
    import useTournamentCenterEntrance from '@/components/tournamentCenter/entrance/useTournamentCenterEntrance';
    import useZoneBanner from '@/components/useZoneBanner';
    import type { ISportInfo } from '@/interface/ISportInfo';
    import { DisplayMode, ProductName, RoutePage, SportType } from '@/interface/enum';
    import { useSportsList } from '@/libraries/composables/useSportsList';
    import { useZone } from '@/libraries/composables/useZone';
    import { platform } from '@/libraries/helpers/platformHelper';
    import {
        getMuayThaiSubLabelKey,
        getSportTypeKey,
        isESports,
        isExchange,
        isImEsports,
        isMuayThaiForDisplay,
        isMuayThaiOneBx,
        isVirtualSport,
    } from '@/libraries/helpers/sportsHelper';
    import { i18n } from '@/libraries/i18n';
    import { invertCapitalize } from '@/libraries/string';
    import { getHost, getSportsUrl } from '@/libraries/url';
    import { disableScrolling, enableScrolling } from '@/libraries/utils/dom';
    import { multipleSportsPathPrefix } from '@/router';
    import { useAccountStore } from '@/stores/accountStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';
    import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';

    interface Props {
        sportsInfo: ISportInfo[];
        loading: boolean;
        isInOtherProduct?: boolean;
        refetch: () => void;
    }

    const props = withDefaults(defineProps<Props>(), {
        isInOtherProduct: false,
        refetch: undefined,
    });

    const emit = defineEmits<(e: 'close') => void>();

    interface ZoneEntryConfig {
        id: string;
        component: Component;
        visible: boolean;
        navigate: () => void;
        props?: Record<string, unknown>;
    }

    const route = useRoute();
    const router = useRouter();
    const { isZoneBannerEnabled } = useZoneBanner();
    const { isInSportsCupZone, isInMultipleSportsZone, isInLeagueZone } = useZone();
    const { isShowTournamentCenterSportListEntrance, route: tournamentCenterRoute, url } = useTournamentCenterEntrance();
    const { redirectToMuayThaiOneBx } = useSportsList();
    const { isSportsCupZoneEnabled, isMultipleSportsZoneEnabled, isEuroSite } = storeToRefs(useFeatureToggleStore());
    const { sportType, isMixParlayMode, isAsi } = storeToRefs(useRootStore());
    const { isIomB2c } = storeToRefs(useAccountStore());

    const zoneEntries = computed<ZoneEntryConfig[]>(() => [
        {
            id: 'sportsCup',
            component: SportsCupSportListEntrance,
            visible: isZoneBannerEnabled.value && isSportsCupZoneEnabled.value,
            navigate: navigateToSportsCupZone,
            props: {},
        },
        {
            id: 'multipleSports',
            component: MultipleSportsSportListEntrance,
            visible: isZoneBannerEnabled.value && isMultipleSportsZoneEnabled.value,
            navigate: navigateToMultipleSportsZone,
            props: {},
        },
        {
            id: 'tournamentCenter',
            component: TournamentCenterSportListEntrance,
            visible: isShowTournamentCenterSportListEntrance.value && !isEuroSite.value,
            navigate: navigateToTournamentCenter,
            props: {},
        },
    ]);

    const visibleZoneEntries = computed(() => zoneEntries.value.filter(e => e.visible));

    watch(() => route.fullPath, () => emit('close'));

    onMounted(() => {
        disableScrolling();
        props.refetch();
        useHeaderDisplayStore().setState({
            isShowWidgetSwitch: false,
            isShowSportsFooter: false,
        });
    });

    onUnmounted(() => {
        enableScrolling();
        useHeaderDisplayStore().setDisplayMode(DisplayMode.ShowAll);
    });

    function getGaTag(info: ISportInfo) {
        const sportName = getSportTypeKey(info.sportType) || '';
        return `JARVIS_Header2_DropDown_FeaturesSports_${sportName}`;
    }

    function hasEvents(info: ISportInfo) {
        return isMixParlayMode.value ? info.hasMixParlayEvents : info.hasEvents;
    }

    function hasLive(info: ISportInfo) {
        return isMixParlayMode.value ? info.hasLiveMixParlayEvents : info.hasLiveEvents;
    }

    function navigateToSportsCupZone() {
        if (!isInSportsCupZone.value) {
            if (props.isInOtherProduct) {
                window.location.href = `${getSportsUrl()}/sports-cup`;
            } else {
                router.push({ name: RoutePage.SportsCupPage });
            }
        }
    }

    function navigateToMultipleSportsZone() {
        if (!isInMultipleSportsZone.value) {
            if (props.isInOtherProduct) {
                window.location.href = `${getSportsUrl()}/${multipleSportsPathPrefix}`;
            } else {
                router.push({ name: RoutePage.MultipleSportsZone });
            }
        }
    }

    function navigateToTournamentCenter() {
        if (props.isInOtherProduct) {
            window.location.href = url.value;
        } else {
            router.push(tournamentCenterRoute.value);
        }
    }

    function handleZoneEntryClick(entry: ZoneEntryConfig) {
        emit('close');
        entry.navigate();
    }

    function switchProduct(productName: ProductName, sportTypeValue?: SportType) {
        platform.switchProduct({ productName, sportType: sportTypeValue });
    }

    function switchProductToExchange() {
        const threeMinutes = new Date(new Date().getTime() + 3 * 60 * 1000);
        Cookies.set('toSportsRoute', JSON.stringify({ name: RoutePage.Exchange }), {
            domain: getHost(),
            expires: threeMinutes,
        });
        switchProduct(ProductName.Sports, SportType.Cricket);
    }

    const productMap: Partial<Record<SportType, number>> = {
        [SportType.VirtualBasketball]: 3,
        [SportType.VirtualFootball]: 6,
        [SportType.ESportsIm]: 9,
    };

    function handleSelectSportsInVirtualSports(info: ISportInfo) {
        if (isMuayThaiOneBx(info.sportType)) {
            redirectToMuayThaiOneBx();
        } else if (isVirtualSport(info.sportType)) {
            switchProduct(ProductName.VirtualSports, productMap[info.sportType]);
        } else if (isImEsports(info.sportType)) {
            switchProduct(ProductName.eSports, productMap[info.sportType]);
        } else if (isExchange(info.sportType)) {
            if (isAsi.value) {
                switchProductToExchange();
            } else {
                platform.showLogin('Sports');
            }
        } else {
            if (!hasEvents(info)) return;

            const sportRouteName = SportType.toRouteName(info.sportType);
            Cookies.set('sportType', sportRouteName, { path: '/', domain: getHost() });

            switchProduct(ProductName.Sports, info.sportType);
        }
    }

    function handleSelectSportsInCasualSite(info: ISportInfo) {
        if (isMuayThaiOneBx(info.sportType)) {
            redirectToMuayThaiOneBx();
        } else if (isVirtualSport(info.sportType)) {
            switchProduct(ProductName.VirtualSports, productMap[info.sportType]);
        } else if (isImEsports(info.sportType)) {
            switchProduct(ProductName.eSports, productMap[info.sportType]);
        } else if (isExchange(info.sportType)) {
            if (isAsi.value) {
                router.push({ name: RoutePage.Exchange });
            } else {
                platform.showLogin('Sports');
            }
        } else {
            if (!hasEvents(info)) return;

            emit('close');
            const isSameRoute = route.name === RoutePage.MainBySport && sportType.value === info.sportType;
            if (!isSameRoute || isInLeagueZone.value) {
                const sportRouteName = SportType.toRouteName(info.sportType);
                router.push({ name: RoutePage.MainBySport, params: { sportRouteName } });
            }
        }
    }

    function selectSport(info: ISportInfo) {
        if (props.isInOtherProduct) {
            handleSelectSportsInVirtualSports(info);
        } else {
            handleSelectSportsInCasualSite(info);
        }
    }
</script>

<style lang="scss" scoped>
@import "@/scss/components/sportsSwitchList";

.hideEsportsIM {
    padding-bottom: 0 !important;
    border-bottom: 0 !important;
    margin-bottom: 0 !important;
}
</style>
